import { Injectable } from '@angular/core';
import { pdf2svg } from '@assets/lib/pdf2svg';
import { text2xml, walkTree } from '@svgeditor/utilities.js';
import { CanvasService } from './canvas.service';
import { MimicsService } from '@service/mimics.service';
import { VesselService } from '@service/vessel.service';
import { AuthService } from '@service/auth.service';
import { Subject } from 'rxjs';
import { EditorService } from '@service/editor.service';
import { MimicFolderService } from '@service/mimic-folder.service';

@Injectable({ providedIn: 'root' })
export class ImportPDFService {
    loadingFinished$: Subject<void> = new Subject();

    constructor(
        private canvasService: CanvasService,
        private mimicService: MimicsService,
        private vesselService: VesselService,
        private authService: AuthService,
        private editorService: EditorService,
        private mimicFolderService: MimicFolderService
    ) {}

    async onSetPDF(event): Promise<void> {
        const fileToLoad = event.target.files[0];
        if (!fileToLoad) return;

        const fileName = fileToLoad.name;
        const self = this;

        return new Promise<void>((resolve, reject) => {
            const fileReader = new FileReader();

            fileReader.onload = async function (event) {
                try {
                    const canvas = self.canvasService.getCanvas();
                    const base64File = event.target.result;
                    const PDFsToSVGs = await pdf2svg(base64File, fileToLoad);
                    let mimicId;
                    for (let page of PDFsToSVGs) {
                        let mimicName = fileName + ' ' + page[1];
                        const svgString = self.getSvgString(page[0], mimicName);
                        mimicId = await self.handleSaveMimic(svgString, mimicName);
                    }
                    const mimic = await self.mimicService.getMimic(mimicId);
                    self.editorService.setCurrentMimic(mimic);
                    self.loadingFinished$.next();
                    resolve();
                } catch (err) {
                    reject(err);
                }
            };

            fileReader.onerror = function (err) {
                reject(err);
            };

            fileReader.readAsDataURL(fileToLoad);
        });
    }

    getSvgString(data: string, fileName) {
        const canvas = this.canvasService.getCanvas();
        const nodes = text2xml(data);
        canvas.prepareSvg(nodes);
        const adopted = document.adoptNode(nodes.children[0]);
        const mimic = {
            name: fileName,
            profile: { width: 1704, height: 864, margin: 10 },
        } as any;
        const svgString = this.canvasService.getEmptySvg(mimic);
        const parser = new DOMParser();
        const svgElement = parser.parseFromString(svgString, 'image/svg+xml') as any;
        const layer = svgElement.getElementsByClassName('layer')[0] as any;
        if (!layer) return;

        walkTree(adopted, el => {
            if (el.id == '') el.id = canvas.getNextId();
        });

        for (let i = adopted.children.length - 1; i >= 0; i--) {
            layer.prepend(adopted.children[i]);
        }

        adopted.remove();
        const s = new XMLSerializer();
        const str = s.serializeToString(svgElement);

        return str;
    }

    private async handleSaveMimic(svgString, fileName) {
        const vesselId = this.vesselService.getActiveVesselId();
        const userId = this.authService.getCurrentUser().oid;
        const profile = {
            bkcolor: '#131B23',
            color: '#bccff2',
            grid: 1,
            allowGrid: false,
            width: 1704,
            height: 864,
            margin: 10,
        };
        try {
            const rootList = await this.mimicFolderService.getByVesselId(vesselId);
            const newMimic = await this.mimicService.create(
                fileName,
                userId,
                vesselId,
                rootList.length,
                null,
                null,
                profile,
                svgString
            );
            if (newMimic) {
                return newMimic.id;
            }
        } catch (err) {
            console.error(err);
            throw err;
        }
    }
}
